import { Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { GridColDef } from '@mui/x-data-grid-pro';
import { SiteMeta } from '@src/types/SiteMeta';
import { TimeMode } from '@types';
import { currentTzString } from '@utils';
import { useMemo } from 'react';
import DeviceNameCell from '../components/Cells/DeviceNameCell';
import HistoricalChartCell from '../components/Cells/HistoricalChartCell';
import TemperatureCell from '../components/Cells/TemperatureCell';
import TimestampCell from '../components/Cells/TimestampCell';
import { COLUMN_FLEX, headerStyles } from '../styles';
import { formatTimestamp } from '../utils/incidentUtils';
import useAlias from '@alias';

interface UseIncidentColumnsReturn extends Array<GridColDef> {}

export const useIncidentColumns = (
  timeMode?: TimeMode,
  siteMeta?: SiteMeta,
): UseIncidentColumnsReturn => {
  const theme = useTheme();
  const alias = useAlias();

  return useMemo(
    () => [
      {
        field: 'deviceName',
        headerName: alias('Device Name'),
        ...COLUMN_FLEX.DEVICE_NAME,
        sortable: true,
        filterable: true,
        renderCell: (params) => (
          <DeviceNameCell params={params} theme={theme} />
        ),
      },
      {
        field: 'unitId',
        headerName: alias('Unit ID'),
        ...COLUMN_FLEX.UNIT_ID,
        sortable: true,
        filterable: true,
      },
      {
        field: 'rackId',
        headerName: alias('Rack ID'),
        ...COLUMN_FLEX.RACK_ID,
        sortable: true,
        filterable: true,
      },
      {
        field: 'maxCellT',
        headerName: alias('Alert Max Cell T (°C)'),
        ...COLUMN_FLEX.TEMPERATURE,
        type: 'number',
        sortable: true,
        filterable: true,
        align: 'center',
        headerAlign: 'center',
        renderHeader: () => (
          <Tooltip title={alias('Current temperature')} placement='top'>
            <p style={headerStyles}>{alias('Max Cell Temperature')}</p>
          </Tooltip>
        ),
        renderCell: (params) => (
          <TemperatureCell params={params} theme={theme} />
        ),
      },
      {
        field: 'timestamp',
        headerName: alias('Incident Time'),
        ...COLUMN_FLEX.TIMESTAMP,
        sortable: true,
        filterable: false,
        renderHeader: () => {
          const timezoneString =
            timeMode && siteMeta
              ? currentTzString({
                  timeMode,
                  lat: siteMeta.GPSLat,
                  long: siteMeta.GPSLong,
                })
              : 'UTC±00:00';

          return (
            <Tooltip title={`${alias('Timezone')}: ${timezoneString}`}>
              <p style={headerStyles}>{alias('Incident Time')}</p>
            </Tooltip>
          );
        },
        renderCell: (params) => (
          <TimestampCell
            params={params}
            formatTimestamp={(timestamp: string) =>
              formatTimestamp(timestamp, timeMode, siteMeta)
            }
          />
        ),
      },
      {
        field: 'historicalData',
        headerName: alias('Max Cell T History'),
        ...COLUMN_FLEX.HISTORICAL_CHART,
        sortable: false,
        filterable: false,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => <HistoricalChartCell params={params} />,
      },
    ],
    [theme, timeMode, siteMeta, alias],
  );
};
